import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { concatLatestFrom } from '@ngrx/operators';
import { tap } from 'rxjs';

import { ToastService } from '../../core/services/toast.service';
import { CartActions } from './cart.actions';
import { selectCartItems } from './cart.selectors';

/** User-facing feedback for cart mutations (runs after the reducer). */
export const cartFeedback$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), toast = inject(ToastService)) =>
    actions$.pipe(
      ofType(CartActions.addToCart),
      concatLatestFrom(() => store.select(selectCartItems)),
      tap(([action, items]) => {
        if (action.stock === 0) {
          toast.show(action.name + ' is out of stock');
          return;
        }
        const qty = items[action.productId] ?? 0;
        if (action.delta > 0 && qty >= action.stock) {
          toast.show('Only ' + action.stock + ' units available');
        } else if (action.delta > 0 && qty > 0) {
          toast.show(action.name + ' · qty ' + qty);
        }
      }),
    ),
  { functional: true, dispatch: false },
);

export const setQtyFeedback$ = createEffect(
  (actions$ = inject(Actions), toast = inject(ToastService)) =>
    actions$.pipe(
      ofType(CartActions.setQty),
      tap((action: ReturnType<typeof CartActions.setQty>) => {
        const requested = parseInt(String(action.raw).replace(/\D/g, ''), 10);
        if (!isNaN(requested) && requested > action.stock) {
          toast.show('Only ' + action.stock + ' units available');
        }
      }),
    ),
  { functional: true, dispatch: false },
);

export const removeFeedback$ = createEffect(
  (actions$ = inject(Actions), toast = inject(ToastService)) =>
    actions$.pipe(
      ofType(CartActions.removeFromCart),
      tap((action: ReturnType<typeof CartActions.removeFromCart>) =>
        toast.show('Removed ' + action.name),
      ),
    ),
  { functional: true, dispatch: false },
);

export const clearFeedback$ = createEffect(
  (actions$ = inject(Actions), toast = inject(ToastService)) =>
    actions$.pipe(
      ofType(CartActions.clearCart),
      tap(() => toast.show('Basket emptied')),
    ),
  { functional: true, dispatch: false },
);
